import { useCallback, useEffect, useMemo, useState } from 'react';

import * as TabelaAPI from '../../../../api/tabela';
import * as OperacaoAPI from '../../../../api/operacao';
import * as SituacaoAPI from '../../../../api/situacao';
import * as UsuarioAPI from '../../../../api/usuario';
import * as HomologationAPI from '../../../../api/homologation';
import CrudsAPI from '../../../../api/CRUDs';
import { parametroByEmpresaId } from '../../../../api/parametro';

export function useHomologationItemModule({ dados, setFlashMessage, mrt, parametro, empresaTabelaId, pessoaId, isAtive }) {
    const { id: homologationId } = dados || {};

    const [isResponsavelHomologacao, setIsResponsavelHomologacao] = useState(false);

    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [gerandoItens, setGerandoItens] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openGerarDialog, setOpenGerarDialog] = useState(false);
    const [openRemoverPessoaDialog, setOpenRemoverPessoaDialog] = useState(false);
    const [pessoaItemParaRemover, setPessoaItemParaRemover] = useState(null);

    const [draft, setDraft] = useState({});

    const [detailByItemId, setDetailByItemId] = useState({});
    const [loadingDetailByItemId, setLoadingDetailByItemId] = useState({});

    const [rowsResumo, setRowsResumo] = useState([]);

    /* -----------------------------
       FUNÇÃO 
    ----------------------------- */

    const consultaTabelaById = useCallback(async (empresaId, tabela, id) => {
        const result = await TabelaAPI.tabelaById(empresaId, tabela, id);
        return result;
    }, []);
    /* -----------------------------
       OPTIONS DO DIALOG
    ----------------------------- */

    const [optionsMenu, setOptionsMenu] = useState([]);
    const [optionsMenuSub, setOptionsMenuSub] = useState([]);
    const [optionsSituacao, setOptionsSituacao] = useState([]);
    const [optionsPapel, setOptionsPapel] = useState([]);
    const [optionsOperacao, setOptionsOperacao] = useState([]);

    /* -----------------------------
       OPTIONS DA GRID (MRT)
    ----------------------------- */

    const [optionsMenuFilter, setOptionsMenuFilter] = useState([]);
    const [optionsMenuSubFilter, setOptionsMenuSubFilter] = useState([]);
    const [optionsSituacaoFilter, setOptionsSituacaoFilter] = useState([]);
    const [optionsOperacaoFilter, setOptionsOperacaoFilter] = useState([]);

    const [allMenus, setAllMenus] = useState([]);

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,

        isError,
        isLoading,
        isRefetching,
        setIsError,
        setIsLoading,
        setIsRefetching,
        rowCount,
        setRowCount,
    } = mrt;

    /* -----------------------------
       CARREGAR OPTIONS INICIAIS
    ----------------------------- */

    const carregarOptionsIniciais = useCallback(async () => {
        try {
            const [menus, situacoes, operacoes] = await Promise.all([
                TabelaAPI.menu(''),
                SituacaoAPI.situacoes(empresaTabelaId),
                OperacaoAPI.retornaOperacoes({}),
            ]);

            setAllMenus(menus || []);

            setOptionsMenu(menus || []);
            setOptionsMenuFilter(
                (menus || []).map((item) => ({
                    text: item.descricao,
                    value: item.descricao,
                }))
            );

            const situacoesFiltradas = (situacoes || []).filter((item) => item.homologationItem === true);

            setOptionsSituacao(situacoesFiltradas);
            setOptionsSituacaoFilter(
                situacoesFiltradas.map((item) => ({
                    text: item.descricao,
                    value: item.descricao,
                }))
            );

            const papel = (situacoes || []).filter((item) => item.homologationPessoa === true);
            setOptionsPapel(papel);

            setOptionsOperacao(operacoes || []);
            setOptionsOperacaoFilter(
                (operacoes || []).map((item) => ({
                    text: item.descricao,
                    value: item.descricao,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    }, [empresaTabelaId]);

    /* -----------------------------
       SUBMENU DO DIALOG
    ----------------------------- */

    const carregarMenuSubPorMenuId = useCallback(async (menuId) => {
        try {
            if (!menuId) {
                setOptionsMenuSub([]);
                return;
            }

            const result = await TabelaAPI.menuSub(menuId, null);
            setOptionsMenuSub(result || []);
        } catch (error) {
            console.error(error);
            setOptionsMenuSub([]);
        }
    }, []);

    /* -----------------------------
       SUBMENU DO FILTRO DA GRID
    ----------------------------- */

    useEffect(() => {
        const consultaMenuSubFiltro = async (menuId) => {
            try {
                const result = await TabelaAPI.menuSub(menuId, null);

                const mapped = (result || []).map((item) => ({
                    text: item.descricao,
                    value: item.descricao,
                }));

                setOptionsMenuSubFilter(mapped);
            } catch (error) {
                console.error(error);
                setOptionsMenuSubFilter([]);
            }
        };

        const filtroMenu = columnFilters.find((filter) => filter.id === 'Menu.descricao');

        if (filtroMenu && filtroMenu.value) {
            const menuEncontrado = allMenus.find((m) => m.descricao === filtroMenu.value);

            if (menuEncontrado) {
                consultaMenuSubFiltro(menuEncontrado.id);
            }
        } else {
            setOptionsMenuSubFilter([]);
        }
    }, [columnFilters, allMenus]);

    /* -----------------------------
       FETCH GRID
    ----------------------------- */

    const fetchData = useCallback(async () => {
        if (!homologationId) return;

        if (!rows.length) setIsLoading(true);
        else setIsRefetching(true);

        try {
            const { data } = await CrudsAPI.homologationItem.listarObj({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters ?? []),
                filtersMode: JSON.stringify(columnFilterFns ?? {}),
                globalFilter: globalFilter ?? '',
                sorting: JSON.stringify(sorting ?? []),
                homologationId,
            });

            setRows(data.data || []);
            setRowCount(data.meta?.totalRowCount || 0);
            setIsResponsavelHomologacao(!!data?.permissions?.isResponsavelHomologacao);
            setIsError(false);
        } catch (error) {
            console.error(error);
            setIsError(true);
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }, [
        homologationId,
        rows.length,
        pagination.pageIndex,
        pagination.pageSize,
        columnFilters,
        columnFilterFns,
        globalFilter,
        sorting,
        setIsError,
        setIsLoading,
        setIsRefetching,
        setRowCount,
    ]);

    /* -----------------------------
       RESUMO DOS ITENS
    ----------------------------- */

    const fetchResumoItens = useCallback(async () => {
        if (!homologationId) return;

        try {
            const { data } = await CrudsAPI.homologationItem.listarObj({
                start: 0,
                size: 9999,
                filters: JSON.stringify([]),
                filtersMode: JSON.stringify({}),
                globalFilter: '',
                sorting: JSON.stringify([]),
                homologationId,
            });

            setRowsResumo(data.data || []);
        } catch (error) {
            console.error(error);
        }
    }, [homologationId]);

    const onEnterTab = useCallback(async () => {
        if (!homologationId) return;

        await fetchData();
        await fetchResumoItens();
    }, [homologationId, fetchData, fetchResumoItens]);

    /* -----------------------------
       CRUD
    ----------------------------- */

    const handleAdicionar = useCallback(() => {
        setDraft({
            ativo: true,
            homologationId,
            dataInicio: new Date().toISOString().substring(0, 10),
            situacaoId: parametro?.situacaoHomologacaoItemId,
        });

        setSelectedRow(null);
        setOpenDialog(true);
    }, [homologationId]);

    const handleEditar = useCallback(
        (row) => {
            const obj = row.original;

            setSelectedRow(obj);

            setDraft({
                ...obj,
                dataInicio: obj.dataInicio ? obj.dataInicio.substring(0, 10) : '',
                dataFim: obj.dataFim ? obj.dataFim.substring(0, 10) : '',
                dataHomologado: obj.dataHomologado ? obj.dataHomologado.substring(0, 10) : '',
            });

            if (obj.menuId) carregarMenuSubPorMenuId(obj.menuId);

            setOpenDialog(true);
        },
        [carregarMenuSubPorMenuId]
    );

    const handleChangeDraft = useCallback(
        async (e) => {
            const { name, value, checked, type } = e.target;

            setDraft((prev) => {
                const next = {
                    ...prev,
                    [name]: type === 'checkbox' ? checked : value,
                };

                if (name === 'menuId') {
                    next.menuSubId = '';
                }

                return next;
            });

            if (name === 'menuId') {
                await carregarMenuSubPorMenuId(value);
                return;
            }

            if (name === 'menuSubId') {
                try {
                    const menuSub = await consultaTabelaById(null, 'menuSub', value);

                    setDraft((prev) => ({
                        ...prev,
                        menuSubId: value,
                        descricao: prev.descricao || menuSub?.descricao || '',
                        objetivoBaseSnapshot: prev.objetivoBaseSnapshot || menuSub?.objetivo || '',
                        operacaoId: prev.operacaoId || menuSub?.operacaoId || '',
                    }));
                } catch (error) {
                    console.error(error);
                }
            }
        },
        [carregarMenuSubPorMenuId, consultaTabelaById]
    );

    const invalidateItemDetail = useCallback((itemId) => {
        if (!itemId) return;

        setDetailByItemId((prev) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
        });

        setLoadingDetailByItemId((prev) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
    }, []);

    const handleSalvarDraft = useCallback(
        async (obj) => {
            try {
                const { pessoasSelecionadas = [], usarPermissoes = false, ...itemPayload } = obj || {};

                const payload = {
                    ...itemPayload,
                    homologationId,
                };

                let response;

                if (payload.uuid) {
                    response = await CrudsAPI.homologationItem.editar(payload);
                } else {
                    response = await CrudsAPI.homologationItem.cadastrar(payload);
                }

                const itemSalvo = response?.data?.data || response?.data?.item || payload;
                const itemId = itemSalvo?.id || payload?.id;

                if (!itemId) {
                    throw new Error('Não foi possível identificar o item salvo para vincular pessoas.');
                }

                if (Array.isArray(pessoasSelecionadas) && pessoasSelecionadas.length > 0) {
                    const respAtuais = await CrudsAPI.homologationPessoa.listarObj({
                        start: 0,
                        size: 999,
                        filters: JSON.stringify([
                            { id: 'homologationId', value: homologationId },
                            { id: 'itemId', value: itemId },
                        ]),
                        filtersMode: JSON.stringify({
                            homologationId: 'equals',
                            itemId: 'equals',
                        }),
                        globalFilter: '',
                        sorting: JSON.stringify([]),
                        homologationId,
                    });

                    const atuais = respAtuais?.data?.data || [];
                    const situacaoPessoaId = obj?.situacaoPessoaId ?? parametro?.situacaoHomologacaoUserId;

                    for (const pessoa of pessoasSelecionadas) {
                        const existe = atuais.some((v) => Number(v.pessoaId) === Number(pessoa.id));

                        if (!existe) {
                            await CrudsAPI.homologationPessoa.cadastrar({
                                ativo: true,
                                homologationId,
                                itemId,
                                pessoaId: Number(pessoa.id),
                                dataEntrada: new Date().toISOString().substring(0, 10),
                                situacaoId: situacaoPessoaId,
                                observacao: payload.objetivoBaseSnapshot ?? null,
                            });
                        }
                    }
                }

                if (usarPermissoes && (!pessoasSelecionadas || pessoasSelecionadas.length === 0)) {
                    console.info('usarPermissoes marcado, aguardando rotina backend automática.');
                }

                setFlashMessage(response.data.message, response.data.type);

                setOpenDialog(false);
                setDraft({});
                setSelectedRow(null);

                await fetchData();
                await fetchResumoItens();
                invalidateItemDetail(itemId);
            } catch (error) {
                console.error(error);
                setFlashMessage(error?.response?.data?.message || error.message || 'Erro ao salvar item!', 'error');
            }
        },
        [homologationId, parametro, fetchData, fetchResumoItens, setFlashMessage, invalidateItemDetail]
    );

    const handleVisualizar = useCallback((row) => {
        setDraft(row.original);
        setOpenDialog(true);
    }, []);

    const handleDeletar = useCallback(async () => {
        try {
            if (!selectedRow?.uuid) return;

            const { data } = await CrudsAPI.homologationItem.deletar({ id: selectedRow.uuid });

            setFlashMessage(data.message, data.type);

            setOpenDeleteDialog(false);
            setSelectedRow(null);

            await fetchData();
            await fetchResumoItens();
        } catch (error) {
            console.error(error);
            setFlashMessage(error?.response?.data?.message || 'Erro ao deletar item!', 'error');
        }
    }, [selectedRow, fetchData, fetchResumoItens, setFlashMessage]);

    const handleCancelar = useCallback(() => {
        setOpenDialog(false);
        setOpenDeleteDialog(false);
        setSelectedRow(null);
        setDraft({});
    }, []);

    const fetchDetailPanel = useCallback(async (itemId) => {
        if (!itemId) return;

        try {
            setLoadingDetailByItemId((prev) => ({
                ...prev,
                [itemId]: true,
            }));

            const resp = await HomologationAPI.detalheItem({ itemId });

            setDetailByItemId((prev) => ({
                ...prev,
                [itemId]: {
                    pessoas: resp?.data?.pessoas || [],
                    registros: resp?.data?.registros || [],
                    assinaturas: resp?.data?.assinaturas || [],
                },
            }));
        } catch (error) {
            console.error('fetchDetailPanel erro geral:', error?.response?.data || error.message || error);
        } finally {
            setLoadingDetailByItemId((prev) => ({
                ...prev,
                [itemId]: false,
            }));
        }
    }, []);

    const handleOpenRemoverPessoaDialog = useCallback((row) => {
        setPessoaItemParaRemover(row);
        setOpenRemoverPessoaDialog(true);
    }, []);

    const handleCloseRemoverPessoaDialog = useCallback(() => {
        setPessoaItemParaRemover(null);
        setOpenRemoverPessoaDialog(false);
    }, []);

    const handleRemoverPessoaDoItem = useCallback(async () => {
        try {
            const vinculoUuid = pessoaItemParaRemover?.uuid;
            const itemId = pessoaItemParaRemover?.itemId;

            if (!vinculoUuid || !itemId) return;

            const { data } = await CrudsAPI.homologationPessoa.deletar({ id: vinculoUuid });

            setFlashMessage(data.message, data.type);

            setPessoaItemParaRemover(null);
            setOpenRemoverPessoaDialog(false);

            invalidateItemDetail(itemId);
            await fetchDetailPanel(itemId);
        } catch (error) {
            console.error(error);
            setFlashMessage(error?.response?.data?.message || 'Erro ao remover pessoa do item!', 'error');
        }
    }, [pessoaItemParaRemover, setFlashMessage, invalidateItemDetail, fetchDetailPanel]);

    const handleGerarItens = useCallback(async () => {
        try {
            setGerandoItens(true);
            setFlashMessage('Gerando itens da homologação...', 'info');

            const data = await HomologationAPI.gerar({
                homologationId,
            });

            setFlashMessage(data.message, data.type);

            await fetchData();
            await fetchResumoItens();
        } catch (error) {
            console.error(error);
            setFlashMessage(error?.response?.data?.message || 'Erro ao gerar itens!', 'error');
        } finally {
            setGerandoItens(false);
        }
    }, [homologationId, fetchData, fetchResumoItens, setFlashMessage]);

    const handleAssinarItem = useCallback(
        async ({ itemId, usuario, senha }) => {
            try {
                const { data } = await UsuarioAPI.assinarHomologacao({
                    empresaId: dados.empresaId,
                    homologationId,
                    itemId,
                    usuario,
                    senha,
                });

                setFlashMessage(data.message, data.type);
                invalidateItemDetail(itemId);
            } catch (error) {
                console.error(error);
                setFlashMessage(error?.response?.data?.message || 'Erro ao assinar item!', 'error');
            }
        },
        [dados?.empresaId, homologationId, setFlashMessage, invalidateItemDetail]
    );

    const handleRemoverAssinaturaItem = useCallback(
        async ({ itemId, usuario, senha }) => {
            try {
                const { data } = await UsuarioAPI.removerAssinaturaHomologacao({
                    empresaId: dados.empresaId,
                    homologationId,
                    itemId,
                    usuario,
                    senha,
                });

                setFlashMessage(data.message, data.type);
                invalidateItemDetail(itemId);
            } catch (error) {
                console.error(error);
                setFlashMessage(error?.response?.data?.message || 'Erro ao remover assinatura!', 'error');
            }
        },
        [dados?.empresaId, homologationId, setFlashMessage, invalidateItemDetail]
    );

    /* -----------------------------
       BUSCA OPERAÇÕES (DIALOG)
    ----------------------------- */

    const buscarOperacoes = useCallback(async (valor) => {
        try {
            if (!valor || valor.length < 2) {
                setOptionsOperacao([]);
                return;
            }

            const result = await OperacaoAPI.operacao(valor);
            setOptionsOperacao(result || []);
        } catch (error) {
            console.error(error);
            setOptionsOperacao([]);
        }
    }, []);

    const handleOpenGerarDialog = useCallback(() => {
        setOpenGerarDialog(true);
    }, []);

    const handleCloseGerarDialog = useCallback(() => {
        setOpenGerarDialog(false);
    }, []);

    const handleConfirmGerarItens = useCallback(async () => {
        try {
            setOpenGerarDialog(false);
            await handleGerarItens();
        } catch (error) {
            console.error(error);
        }
    }, [handleGerarItens]);

    useEffect(() => {
        carregarOptionsIniciais();
    }, [carregarOptionsIniciais]);

    useEffect(() => {
        if (!isAtive) return;
        onEnterTab();
    }, [isAtive, onEnterTab]);

    return {
        state: {
            homologationId,
            rows,
            rowsResumo,
            selectedRow,
            gerandoItens,
            detailByItemId,
            loadingDetailByItemId,
        },

        actions: {
            fetchData,
            fetchResumoItens,
            onEnterTab,
            handleAdicionar,
            handleEditar,
            handleSalvarDraft,
            handleVisualizar,
            handleDeletar,
            handleCancelar,
            handleChangeDraft,
            handleGerarItens,
            buscarOperacoes,
            setSelectedRow,
            setOpenDeleteDialog,
            fetchDetailPanel,
            invalidateItemDetail,
            handleAssinarItem,
            handleRemoverAssinaturaItem,
            handleOpenGerarDialog,
            handleCloseGerarDialog,
            handleConfirmGerarItens,
            handleRemoverPessoaDoItem,
            handleOpenRemoverPessoaDialog,
            handleCloseRemoverPessoaDialog,
        },

        dialogs: {
            openDialog,
            setOpenDialog,
            openDeleteDialog,
            setOpenDeleteDialog,
            openGerarDialog,
            setOpenGerarDialog,
            openRemoverPessoaDialog,
            setOpenRemoverPessoaDialog,
            pessoaItemParaRemover,
            setPessoaItemParaRemover,

            draft,
            setDraft,
        },

        mrt: {
            ...mrt,
            isError,
            isLoading,
            isRefetching,
            rowCount,
        },

        options: {
            optionsMenu,
            optionsMenuSub,
            optionsSituacao,
            optionsPapel,
            optionsOperacao,
            optionsMenuFilter,
            optionsMenuSubFilter,
            optionsSituacaoFilter,
            optionsOperacaoFilter,
        },

        context: {
            pessoaId,
            isResponsavelHomologacao,
        },
    };
}
